import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { RotateCcw, Shield, CheckCircle, XCircle, Mail, ChevronRight, AlertCircle } from 'lucide-react';

export default function Returns() {
  return (
    <>
      <Helmet>
        <title>Tagastamine ja garantii | Tõuks.ee</title>
        <meta name="description" content="Tõuks.ee tagastamis- ja garantiitingimused. 14-päevane tagastamisõigus. Kirjuta ametliktouks@gmail.com. Kõigil toodetel tootja garantii." />
        <link rel="canonical" href="https://xn--tuks-0qa.ee/garantii-ja-tagastamine" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Kodu', item: 'https://xn--tuks-0qa.ee/' },
            { '@type': 'ListItem', position: 2, name: 'Tagastamine ja garantii', item: 'https://xn--tuks-0qa.ee/garantii-ja-tagastamine' },
          ],
        })}</script>
      </Helmet>

      <div className="container mx-auto py-12 md:py-20 px-4 md:px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Tagastamine ja garantii</h1>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Ostsid midagi, mis ei sobi? Pole muret — selgitame tagastusprotsessi sammhaaval.
          </p>
        </div>

        {/* Quick overview cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
          {[
            {
              icon: <RotateCcw className="w-6 h-6 text-[#4FC1E9]" />,
              title: '14 päeva',
              desc: 'Tagastamisõigus alates kauba kättesaamise päevast',
            },
            {
              icon: <Shield className="w-6 h-6 text-[#4FC1E9]" />,
              title: '2 aasta garantii',
              desc: 'Seaduslik garantii tootmisdefektide vastu',
            },
            {
              icon: <Mail className="w-6 h-6 text-[#4FC1E9]" />,
              title: 'Vastame 15 päeva',
              desc: 'Pretensioonile vastame 15 tööpäeva jooksul',
            },
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center">
              <div className="bg-[#4FC1E9]/10 rounded-xl p-3 w-fit mx-auto mb-3">{c.icon}</div>
              <p className="font-bold text-gray-800 text-xl mb-1">{c.title}</p>
              <p className="text-gray-500 text-sm">{c.desc}</p>
            </div>
          ))}
        </div>

        {/* Return process */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <RotateCcw className="w-6 h-6 text-[#4FC1E9]" />
            Kuidas tagastada?
          </h2>
          <div className="space-y-6">
            {[
              {
                step: '1',
                title: 'Saada meile e-kiri',
                desc: 'Kirjuta ametliktouks@gmail.com 14 päeva jooksul alates kauba kättesaamise päevast. Lisa kirja tellimuse number ja lühike selgitus tagastamise põhjuse kohta.',
              },
              {
                step: '2',
                title: 'Oota kinnitust',
                desc: 'Vastame sulle 1–2 tööpäeva jooksul ja anname tagastamisjuhised. Defektse toote puhul katame tagastuskulud meie.',
              },
              {
                step: '3',
                title: 'Saada toode tagasi',
                desc: 'Pakenda toode hoolikalt originaalpakendisse ja saada meile antud aadressile. Toode peab olema kasutamata ja originaalpakendis.',
              },
              {
                step: '4',
                title: 'Raha tagastus',
                desc: 'Pärast toote kättesaamist ja seisukorra kontrollimist tagastame raha sinu arveldusarvele 14 päeva jooksul.',
              },
            ].map((s) => (
              <div key={s.step} className="flex gap-4">
                <div className="w-8 h-8 bg-[#4FC1E9] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                  {s.step}
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-1">{s.title}</p>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* What can and cannot be returned */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-green-50 rounded-2xl p-6 border border-green-100">
            <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Saab tagastada
            </h3>
            <ul className="space-y-2 text-sm text-green-700">
              {[
                'Kasutamata toode originaalpakendis',
                'Defektne või vigane toode',
                'Vale toode (meie viga)',
                'Toode, mis ei vasta kirjeldusele',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-red-50 rounded-2xl p-6 border border-red-100">
            <h3 className="font-bold text-red-800 mb-4 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              Ei saa tagastada
            </h3>
            <ul className="space-y-2 text-sm text-red-700">
              {[
                'Kasutatud toode (sõidetud tõuks)',
                'Kahjustatud toode (kasutaja põhjustatud)',
                'Toode ilma originaalpakendita (kui pakend on vajalik)',
                'Toode, mis on tagastustähtajast möödunud',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Warranty info */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#4FC1E9]" />
            Garantii
          </h2>
          <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
            <p>
              Kõigile Tõuks.ee-st ostetud toodetele kehtib <strong>2-aastane seaduslik garantii</strong> vastavalt 
              Eesti Vabariigi Võlaõigusseadusele. Garantii katab tootmisdefekte, mis ilmnevad kahe aasta jooksul 
              alates ostu kuupäevast.
            </p>
            <p>
              Lisaks seaduslikule garantiile kehtib paljudel toodetel tootja garantii, mille täpsed tingimused 
              on märgitud toote juures.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800 mb-1">Garantii ei kata</p>
                <ul className="text-amber-700 space-y-1">
                  <li>• Normaalset kulumist (rattad, griptape, klamber)</li>
                  <li>• Kukkumisest või valest kasutusest põhjustatud kahjustusi</li>
                  <li>• Modifitseeritud või parandatud tooteid</li>
                </ul>
              </div>
            </div>
            <p>
              Garantiiprobleemi korral kirjuta meile <strong>ametliktouks@gmail.com</strong> — 
              lisa kirjale foto defektist ja tellimuse number. Pretensioonile vastame 15 tööpäeva jooksul.
            </p>
          </div>
        </div>

        {/* Legal note */}
        <div className="bg-gray-50 rounded-2xl p-5 mb-8 text-sm text-gray-500">
          <p>
            Vaidluste korral on kliendil õigus pöörduda{' '}
            <a href="https://komisjon.ee" target="_blank" rel="noopener noreferrer" className="text-[#4FC1E9] hover:underline">
              Tarbijavaidluste komisjoni
            </a>{' '}
            poole (komisjon.ee) või EL vaidluste lahendamise platvormi{' '}
            <a href="https://ec.europa.eu/odr" target="_blank" rel="noopener noreferrer" className="text-[#4FC1E9] hover:underline">
              ec.europa.eu/odr
            </a>{' '}
            kaudu.
          </p>
        </div>

        {/* CTA */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { to: '/kontakt', label: 'Kirjuta meile', sub: 'ametliktouks@gmail.com' },
            { to: '/muugitingimused', label: 'Müügitingimused', sub: 'Täielikud tingimused' },
          ].map(({ to, label, sub }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center justify-between bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:border-[#4FC1E9] transition-colors group"
            >
              <div>
                <p className="font-semibold text-gray-800 group-hover:text-[#4FC1E9] transition-colors">{label}</p>
                <p className="text-gray-500 text-sm mt-1">{sub}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#4FC1E9] transition-colors" />
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}
